import React from "react";
import { HashLink as Link } from "react-router-hash-link";

import {
  Header as CarbonHeader,
  HeaderGlobalAction,
  HeaderGlobalBar,
  HeaderMenuButton,
  HeaderMenuItem,
  HeaderName,
  HeaderNavigation,
  HeaderSideNavItems,
  SideNav,
  SideNavItems,
  SideNavLink,
  SideNavMenu,
  SideNavMenuItem,
  SkipToContent,
} from "carbon-components-react";
import { Logout20 } from "@carbon/icons-react";

import { AppShape } from "@eave/shared/types/api";
import AppSummaryHeader from "underwriter_dashboard/shared/AppSummaryHeader";
import { isProduction, isStaging, isHotfix, isIntegration, isDevelopment } from "config";

import ConditionsPanel from "./ToolPanel/ConditionsPanel";
import FlagsPanel from "./ToolPanel/FlagsPanel";
import NotesPanel from "./ToolPanel/NotesPanel";
import ToolPanel from "./ToolPanel";
import Tools from "./Tools";
import { OrderedGroups, ToolPanelEnum } from "./shared";
import Toast, { useToast } from "../Toast";

const styles = require("./styles.module.scss");

export interface HeaderLink {
  name: string;
  to: string;
  pathIsCurrentPage?: Function;
}

// NOTE: jjw: needs to be class (not FC) because Carbon caller will give ref
class HeaderMenuItems extends React.Component<{ headerLinks: HeaderLink[] }, any> {
  render() {
    return (
      <>
        {this.props.headerLinks.map(link => {
          const { name, to, pathIsCurrentPage } = link;

          const path = window.location.pathname;

          let curr: boolean;
          if (typeof pathIsCurrentPage === "function") {
            curr = pathIsCurrentPage(path);
          } else {
            curr = path.startsWith(to);
          }

          return (
            <HeaderMenuItem key={name} element={Link} to={to} isCurrentPage={curr}>
              {name}
            </HeaderMenuItem>
          );
        })}
      </>
    );
  }
}

interface IHeaderProps {
  app: AppShape | null;
  titleLink: HeaderLink;
  headerLinks?: HeaderLink[];
  navExpanded: boolean;
  firstName: string;
  onToggleNavExpanded: Function;
  onLogOut: Function;
}

const Header: React.FC<IHeaderProps> = ({
  app,
  titleLink,
  headerLinks,
  navExpanded,
  firstName,
  onToggleNavExpanded,
  onLogOut = () => {},
}: IHeaderProps) => {
  const GLOBAL_NAME = "TRUS";
  const loanAppInContext = !!(app && app.id); // only show nav sidebar on loan app pages

  const [toolPanelOpenTo, setToolPanelOpenTo] = React.useState<null | ToolPanelEnum>(null);
  const toggleToolPanelOpenTo = (clicked: ToolPanelEnum) => {
    setToolPanelOpenTo(toolPanelOpenTo === clicked ? null : clicked);
  };

  const { toastHooks, notifyFunctions } = useToast();
  const { notifySuccess, notifyError } = notifyFunctions;

  const getToolPanelComponent = (click: ToolPanelEnum) => {
    if (click === ToolPanelEnum.Flags) {
      return <FlagsPanel appId={app.id} />;
    }
    if (click === ToolPanelEnum.Conditions) {
      return (
        <ConditionsPanel appId={app.id} notifySuccess={notifySuccess} notifyError={notifyError} />
      );
    }
    if (click === ToolPanelEnum.Notes) {
      return <NotesPanel appId={app.id} />;
    }
    return React.Fragment;
  };

  // if no loan app in context, apply additional class to header nav to keep the nav links from
  // being hidden under a certain min-width (with no loan app, there's no hamburger menu for these
  // links to migrate to under this width, which is the behavior that carbon intends by default)
  // see: https://www.carbondesignsystem.com/components/UI-shell-header/usage/#responsive-behavior
  let headerNavClass = "";
  if (!loanAppInContext) {
    headerNavClass = styles.alwaysShowHeaderNavLinks;
  }

  return (
    <>
      {!isProduction() && (
        <div className={`${styles.nonProdTopLine} environment-indicator`}>
          {isHotfix() && <strong>Hotfix</strong>}
          {isStaging() && !isHotfix() && <strong>Staging</strong>}
          {isIntegration() && <strong>Integration</strong>}
          {isDevelopment() && <strong>Development</strong>}
        </div>
      )}

      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <Toast {...toastHooks} />

      <CarbonHeader aria-label={`${GLOBAL_NAME} ${titleLink.name}`}>
        <SkipToContent />

        {loanAppInContext && (
          <HeaderMenuButton
            aria-label="Open menu"
            isCollapsible
            onClick={onToggleNavExpanded}
            isActive={navExpanded}
          />
        )}

        <HeaderName
          element={Link}
          to={titleLink.to}
          prefix={GLOBAL_NAME}
          className={styles.headerName}
        >
          {titleLink.name}
        </HeaderName>

        <HeaderNavigation
          className={headerNavClass}
          aria-label={`${GLOBAL_NAME} ${titleLink.name}`}
        >
          {headerLinks && <HeaderMenuItems headerLinks={headerLinks} />}
        </HeaderNavigation>

        <HeaderGlobalBar>
          {app && (
            <Tools
              appId={app.id}
              panelOpenTo={toolPanelOpenTo}
              togglePanelOpenTo={toggleToolPanelOpenTo}
            />
          )}
          <div className={styles.greeting}>Hi, {firstName}!</div>
          <HeaderGlobalAction
            aria-label="Log out"
            title="Log out"
            onClick={onLogOut}
            style={{ justifyContent: "center" }}
          >
            <Logout20 />
          </HeaderGlobalAction>
        </HeaderGlobalBar>

        {toolPanelOpenTo && (
          <ToolPanel
            appId={app.id}
            panelOpenTo={toolPanelOpenTo}
            hidePanel={() => toggleToolPanelOpenTo(toolPanelOpenTo)}
          >
            {getToolPanelComponent(toolPanelOpenTo)}
          </ToolPanel>
        )}

        {loanAppInContext && (
          <SideNav isFixedNav aria-label="Side navigation" expanded={navExpanded}>
            <SideNavItems>
              {headerLinks && (
                <HeaderSideNavItems hasDivider>
                  <HeaderMenuItems headerLinks={headerLinks} />
                </HeaderSideNavItems>
              )}

              <SideNavLink element={Link} to={`/underwriter/app/${app.id}`}>
                {app.id}&ndash;{app.borrower1_last_name}
              </SideNavLink>

              {OrderedGroups.map(group => (
                <SideNavMenu
                  key={group.name}
                  renderIcon={group.icon}
                  title={group.name}
                  className={styles.sideNavMenuItem}
                >
                  {group.links.map(link => {
                    const linkTo = `/underwriter/app/${app.id}${
                      link && link.to && link.to.startsWith("#") ? "" : "/"
                    }${link && link.to}`;
                    return (
                      link && (
                        <SideNavMenuItem
                          key={link.name}
                          element={Link}
                          to={linkTo}
                          aria-current={
                            window.location &&
                            window.location.pathname &&
                            window.location.pathname.startsWith(linkTo)
                              ? "page"
                              : ""
                          }
                        >
                          <span title={link.name} className=".bx--text-truncate--end">
                            {link.name}
                          </span>
                        </SideNavMenuItem>
                      )
                    );
                  })}
                </SideNavMenu>
              ))}
            </SideNavItems>
          </SideNav>
        )}
      </CarbonHeader>
      {loanAppInContext && <AppSummaryHeader app={app} navExpanded={navExpanded} />}
    </>
  );
};

export default Header;
